import { useEffect, useState } from "react";

import { Table, Row, Col, Button, Input, message, Select } from "antd";

import { ColumnsType } from "antd/lib/table";
import { PagedResult } from "states/paged-result-model";
import { useDebouncedCallback } from "use-debounce/lib";

import {
  SearchProductRequestModel,
  ProductModel,
} from "states/product/product.models";
import {
  getAllVariationsApi,
  searchProductApi,
} from "states/product/product.api";
import { ProductCategoryModel } from "states/product-category/product-category.models";
import { getProductCategoryListApi } from "states/product-category/product-category.api";
import { t, CoreResource } from "shared/plugins/localization";
import { DefaultErrorMessage } from "shared/constants/define-localization";
import { RemovedEntityModel } from "shared/models/removed-entity-model";
import { formatMoney } from "shared/plugins/string-helper";
import { useDispatch } from "react-redux";
import { WareHouseModel } from "states/ware-house/ware-house.models";
import { getWareHouseList } from "states/ware-house/ware-house.api";
import { setProductStockModal } from "states/global-value/global-value.slice";
import { SearchOutlined } from "@ant-design/icons";
import { ProductName } from "shared/components/product/product-name";

const { Option } = Select;
const ProductStockPage = () => {
  const dispatch = useDispatch();
  const [selectedRowsKeys, setSelectedRowsKeys] = useState<
    RemovedEntityModel<number>
  >({
    idList: [],
  });
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [pagedResults, setPagedResults] = useState(
    {} as PagedResult<ProductModel>
  );
  const [filterModel, setFilterModel] = useState<SearchProductRequestModel>({
    includeAccessory: true,
    includeChildrenUnitProduct: true,
    includeChildrenVariationProduct: true,
    page: 1,
    pageSize: 20,
  });
  const [productCategories, setProductCategories] = useState<
    ProductCategoryModel[]
  >([]);
  const [wareHouses, setWareHouses] = useState<WareHouseModel[]>([]);

  const [variationModal, setVariationModal] = useState<{
    model: ProductModel[];
    visible: boolean;
    onClose: (isLoadingList?: boolean) => void;
  }>({
    model: [] as ProductModel[],
    visible: false,
    onClose: (isLoadingList?: boolean) => {
      setVariationModal({ ...variationModal, visible: false });
      if (isLoadingList) {
        gotoPage(1,20);
      }
    },
  });

  const rowSelection = {
    selectedRowsKeys,
    onChange: (selectedRowKeys: any) => {
      setSelectedRowsKeys({ ...selectedRowsKeys, idList: selectedRowKeys });
    },
  };
  const getProductCategoryies = async () => {
    try {
      const res = await getProductCategoryListApi();
      setProductCategories(res.data);
    } catch (error) {
      message.error(error?.response?.data?.error?.message ?? "Lỗi hệ thống");
    }
  };
  const onEmptySelection = () => {
    setSelectedRowsKeys({ ...selectedRowsKeys, idList: [] });
  };

  const gotoPage = async (page: number, pageSize:number) => {
    setIsLoadingList(true);
    const result = await searchProductApi({ ...filterModel, page,pageSize });
    result.data.results.forEach((m) => (m.key = m.id.toString()));
    setPagedResults(result.data);
    setIsLoadingList(false);
  };
  const handleSearch = useDebouncedCallback(() => {
    gotoPage(1,20);
  }, 500);

  const _handleClickVariationModal = async (product: ProductModel) => {
    const res = await getAllVariationsApi(product.id);
    setVariationModal({ ...variationModal, model: res.data, visible: true });
  };

  const columns: ColumnsType<ProductModel> = [
    {
      title: "Mã hàng",
      dataIndex: "skuCode",
      key: "skuCode",
      width: "150px",
      render: (skuCode: string, item) => skuCode,
    },
    {
      title: "Tên sản phẩm",
      dataIndex: "websiteName",
      key: "websiteName",
      render: (websiteName: string, item) => <ProductName product={item} />,
    },
    {
      title: "Giá bán",
      dataIndex: "price",
      key: "price",
      render: (price: string, item) => formatMoney(item.price),
    },
    {
      title: "Giá nhập",
      dataIndex: "costPrice",
      key: "costPrice",
      render: (costPrice: string, item) => formatMoney(item.costPrice),
    },
    {
      title: "Tồn kho",
      dataIndex: "allstock",
      key: "allstock",
      render: (allstock: string, item) => (
        <>
          Tồn{" "}
          {item.inventories
            .map((m) => m.totalStockQuantity)
            .reduce((a, b) => a + b, 0)}{" "}
          <Button
            onClick={() => {
              dispatch(
                setProductStockModal({
                  show: true,
                  productStocks: item.inventories,
                })
              );
            }}
            type="link"
            icon={<SearchOutlined />}
          />
        </>
      ),
    },
    {
      title: "Tồn thực tế",
      dataIndex: "stock",
      key: "stock",
      render: (stock: string, item) => (
        <>
          Tồn{" "}
          {item.inventories
            .map((m) => m.onHandQuantity)
            .reduce((a, b) => a + b, 0)}
        </>
      ),
    },
  ];

  useEffect(() => {
    handleSearch();
  }, [filterModel]);

  useEffect(() => {
    getProductCategoryies();
    getWareHouseList().then((res) => {
      setWareHouses(res.data);
    });
    document.title = "Check tồn kho";
  }, []);

  return (
    <div className="product">
      <Row gutter={[16, 16]} className="mb-4">
        <Col span={4}>
          <Input.Search
            value={filterModel.keyword}
            placeholder={"Tìm kiếm sản phẩm"}
            onChange={(e) => {
              setFilterModel({ ...filterModel, keyword: e.target.value });
            }}
          ></Input.Search>
        </Col>
        <Col span={4}>
          <Select
            className="w-100"
            placeholder="Chọn danh mục"
            onChange={(value: number) => {
              setFilterModel({
                ...filterModel,
                categoryIds: value ? [value] : [],
              });
            }}
            allowClear
            value={
              filterModel?.categoryIds && filterModel.categoryIds.length > 0
                ? filterModel.categoryIds[0]
                : undefined
            }
            showSearch
            filterOption={(input, option: any) =>
              option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            {productCategories &&
              productCategories.map((productCate) => (
                <Option key={productCate.id} value={productCate.id}>
                  {productCate.name}
                </Option>
              ))}
          </Select>
        </Col>
        <Col span={4}></Col>
        <Col span={12} className="d-flex justify-content-end"></Col>
      </Row>
      <Table
        rowSelection={rowSelection}
        columns={columns}
        dataSource={pagedResults.results}
        size="small"
        pagination={{
          total: pagedResults.totalItemCount,
          pageSize: pagedResults.pageSize,
          current: pagedResults.currentPage,
          onChange: gotoPage,
        }}
        loading={isLoadingList}
      ></Table>
    </div>
  );
};

export default ProductStockPage;
